/**
 * imageIntake.js — screenshot bet intake (production version of the
 * bet-hub prototype's contract stub).
 *
 *   image ──► extractFieldsFromImage() ──► extraction object
 *         ──► imageDraftFromExtraction() ──► { draft, missing, warnings }
 *         ──► the same confirm card the text/link paths use.
 *
 * The extractor is the betslip-image Netlify function: it sends the
 * screenshot to a vision model, validates the structured reply
 * server-side, and returns the extraction below. API keys stay
 * server-side. PRIVACY: the screenshot is processed in memory by the
 * function and discarded — never written to Firestore or storage.
 *
 * Extraction object (what the extractor returns):
 *   { fields: { bookKey?, bookName?, sport?, event?, selection?,
 *               betType?, line?, oddsAmerican?, stake?,
 *               legs?: [{ event?, selection?, betType?, line? }] },
 *     confidence?: { [field]: 0..1 } }
 *
 * One extension over the prototype stub: `fields.legs` for parlays,
 * handled exactly like linkParse's parlay shares (the draft becomes a
 * parlay whose legs the confirm card edits individually).
 *
 * Low-confidence fields surface in `warnings` so the confirm card can
 * flag them — the same never-guess rule as the other ingest paths.
 */

import { createBet } from '../engine/model.js';

/** Below this per-field confidence, a value is flagged for review. */
export const IMAGE_CONFIDENCE_FLOOR = 0.8;

/**
 * The extractor seam — now real. `image` is { dataUrl } (the tracker
 * downscales to <=1024px wide on a canvas before upload); `options`
 * carries the function endpoint + the user's Firebase ID token.
 * Resolves (never throws) to:
 *   { ok: true, extraction }
 *   { ok: false, enabled: false, error }  — screenshot reading is not
 *     switched on server-side yet (no ANTHROPIC_API_KEY configured)
 *   { ok: false, error }                  — unreadable / unavailable
 */
export async function extractFieldsFromImage(image, options = {}) {
  const endpoint = options.endpoint || '/.netlify/functions/betslip-image';
  try {
    const dataUrl = String((image && image.dataUrl) || '');
    const comma = dataUrl.indexOf(',');
    if (comma === -1) {
      return { ok: false, error: 'That image could not be prepared for upload.' };
    }
    const mediaType = (dataUrl.slice(0, comma).match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.idToken ? { Authorization: `Bearer ${options.idToken}` } : {})
      },
      body: JSON.stringify({ image: dataUrl.slice(comma + 1), mediaType })
    });
    const payload = await response.json().catch(() => null);
    if (!payload) {
      return { ok: false, error: 'The screenshot reader did not respond — paste the slip text instead.' };
    }
    if (payload.enabled === false) {
      return { ok: false, enabled: false, error: payload.error };
    }
    if (!payload.ok) {
      return { ok: false, error: payload.error || 'We couldn\'t read that screenshot.' };
    }
    return { ok: true, extraction: payload.extraction };
  } catch (err) {
    return { ok: false, error: 'The screenshot reader is unavailable — paste the slip text instead.' };
  }
}

/**
 * Shape an extraction object into the standard draft contract
 * ({ draft, missing, warnings }), identical in form to parseBetText /
 * parseSharePage output.
 */
export function imageDraftFromExtraction(extraction) {
  const warnings = [];
  const missing = [];
  const fields = (extraction && extraction.fields) || {};
  const confidence = (extraction && extraction.confidence) || {};

  for (const [field, value] of Object.entries(confidence)) {
    if (typeof value === 'number' && value < IMAGE_CONFIDENCE_FLOOR) {
      warnings.push('low-confidence read for ' + field + ' (' + Math.round(value * 100) + '%) — verify it');
    }
  }
  warnings.push('read from a screenshot by AI — verify every field before saving');

  const book = fields.bookKey
    ? { key: fields.bookKey, name: fields.bookName ?? fields.bookKey }
    : (fields.bookName ? { key: 'unknown', name: fields.bookName } : undefined);

  const legs = Array.isArray(fields.legs) && fields.legs.length > 1
    ? fields.legs.map((leg) => ({
      event: leg.event ?? '',
      selection: leg.selection ?? '',
      betType: leg.betType ?? 'moneyline',
      ...(typeof leg.line === 'number' ? { line: leg.line } : {})
    }))
    : null;

  const draft = createBet({
    book,
    sport: fields.sport ?? '',
    league: fields.sport ?? '',
    event: legs ? legs.length + '-leg parlay' : (fields.event ?? ''),
    selection: legs
      ? legs.map((l) => l.selection).join(' + ')
      : (fields.selection ?? ''),
    betType: legs ? 'parlay' : (fields.betType ?? 'moneyline'),
    legs: legs ?? undefined,
    line: typeof fields.line === 'number' ? fields.line : null,
    oddsAmerican: typeof fields.oddsAmerican === 'number' ? fields.oddsAmerican : undefined,
    stake: typeof fields.stake === 'number' ? fields.stake : 0,
    status: 'draft',
    source: 'image'
  });
  if (typeof fields.oddsAmerican !== 'number') { draft.oddsAmerican = null; missing.push('odds'); }
  if (typeof fields.stake !== 'number') missing.push('stake');
  if (!book) missing.push('book');
  if (!legs && !fields.selection) missing.push('selection');

  return { draft, missing, warnings };
}
